import { useMemo } from 'react';
import { useSelectedPartner } from 'src/graphql';
import { useNormalizedCustomAttributeDefinitions } from 'src/graphql/entities/custom-attributes/hooks/use-normalized-custom-attribute-definitions';
import {
  SUPPORT_POLICY_CONDITION_FIELDS,
  SupportPolicyConditionField,
} from 'src/graphql/entities/support-policy/support-policy.conditions';
import { buildCustomPropertyConditionFields } from 'src/graphql/entities/support-policy/support-policy.custom-property-fields';

export interface SupportPolicyConditionFields {
  fields: SupportPolicyConditionField[];
  /** keyed by the wire attribute (`field.value`) — what `deserializeSupportPolicyConditions` resolves saved rules against */
  fieldMap: Map<string, SupportPolicyConditionField>;
  isLoading: boolean;
}

/**
 * Built-in condition fields plus one field per conversation / site custom property the current
 * partner has defined. Uses the selected partner like the built-in option queries do, and the same
 * visibility filter the side panel applies.
 */
export function useSupportPolicyConditionFields(): SupportPolicyConditionFields {
  const { selectedPartner } = useSelectedPartner();
  const partnerId = selectedPartner?.partnerId ?? undefined;

  const conversationDefinitions = useNormalizedCustomAttributeDefinitions({
    partnerId,
    entity: 'Conversation',
  });
  const addressDefinitions = useNormalizedCustomAttributeDefinitions({
    partnerId,
    entity: 'Address',
  });

  return useMemo(() => {
    const fields = [
      ...SUPPORT_POLICY_CONDITION_FIELDS,
      ...buildCustomPropertyConditionFields(
        'Conversation',
        conversationDefinitions.visibleDefinitions,
      ),
      ...buildCustomPropertyConditionFields('Address', addressDefinitions.visibleDefinitions),
    ];

    return {
      fields,
      fieldMap: new Map(fields.map((field) => [field.value, field])),
      isLoading: conversationDefinitions.loading || addressDefinitions.loading,
    };
  }, [
    conversationDefinitions.visibleDefinitions,
    conversationDefinitions.loading,
    addressDefinitions.visibleDefinitions,
    addressDefinitions.loading,
  ]);
}
